'use client';

import Link from '@/components/ui/Link';

interface NotFound404Props {
  title?: string;
  subtitle?: string;
  message?: string;
}

/**
 * Styled 404 page component displayed when a section URL is not found
 */
export default function NotFound404({
  title = '404',
  subtitle = 'பக்கம் கிடைக்கவில்லை',
  message = 'நீங்கள் தேடும் பக்கம் இல்லை அல்லது நீக்கப்பட்டிருக்கலாம்.',
}: NotFound404Props) {
  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-16 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Large 404 Text */}
      <div className="relative">
        <h1 className="text-[150px] md:text-[200px] font-black text-gray-200 dark:text-gray-700 leading-none select-none">
          {title}
        </h1>
        {/* Overlay decoration */}
        <div className="absolute inset-0 flex items-center justify-center">
          <svg
            className="w-32 h-32 md:w-40 md:h-40 text-red-500 opacity-80"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
      </div>

      {/* Subtitle */}
      <h2 className="text-2xl md:text-4xl font-bold text-gray-700 dark:text-gray-200 mb-4 text-center -mt-4">
        {subtitle}
      </h2>

      {/* Message */}
      <p className="text-gray-500 dark:text-gray-400 text-center max-w-lg mb-8 text-lg px-4">
        {message}
      </p>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Go Back Button */}
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center justify-center px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          பின் செல்
        </button>

        {/* Home Button */}
        <Link
          href="/"
          className="inline-flex items-center justify-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          முகப்புக்குச் செல்
        </Link>
      </div>

      {/* Decorative Elements */}
      <div className="mt-12 flex items-center gap-2 text-gray-400 dark:text-gray-500">
        <div className="w-16 h-px bg-gray-300 dark:bg-gray-600"></div>
        <span className="text-sm">தினமணி</span>
        <div className="w-16 h-px bg-gray-300 dark:bg-gray-600"></div>
      </div>
    </div>
  );
}
